/* eslint-disable @typescript-eslint/no-non-null-assertion */
import axios from 'axios';

import { getAccessToken } from '../utils/portal-token.util';

const userDataToken = () => localStorage.getItem('user-data-token');

const getDefaultHeaders = () => ({
  'Access-Control-Allow-Origin': '*',
  Authorization: `Bearer ${getAccessToken()}`,
  'user-data-info': userDataToken(),
  client_id: process.env.REACT_APP_SENSEDIA_CLIENT_ID || '',
  'Content-Type': 'application/json;charset=utf-8'
});

export const apiSensedia = axios.create({
  baseURL: process.env.REACT_APP_API_SENSEDIA_URL,
  headers: { ...getDefaultHeaders(), newendpoint: true },
  transformRequest: [
    function (data, headers) {
      headers!['Authorization'] = `Bearer ${getAccessToken()}`;
      headers!['user-data-info'] = userDataToken();
      return JSON.stringify(data);
    }
  ]
});

export const refreshToken = async () => {
  try {
    const accessToken = getAccessToken();
    const refreshToken = localStorage.getItem('refreshToken');

    localStorage.setItem('tokenCreatedAt', String(Date.now()));

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    });

    const url = `${process.env.REACT_APP_SENSEDIA_OAUTH_API}/refresh-token`;

    const response = await axios.post(url, params.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        // O token vem do Portal do Corretor, emitido por outro client_id: o gateway só
        // renova com o Bearer do próprio token (Basic com a credencial da SPA dá 500 no
        // Apigee e 401 na Sensedia). O tipo-usuario é obrigatório junto do Bearer.
        Authorization: `Bearer ${accessToken}`,
        'tipo-usuario': 'C'
      }
    });

    const newAccessToken = response.data.access_token;
    const newRefreshToken = response.data.refresh_token;

    localStorage.setItem('tokenPortal', newAccessToken);
    localStorage.setItem('refreshToken', newRefreshToken);

    // O expires_in do Apigee é bem menor que o da Sensedia e varia por grant: guardar o
    // valor devolvido pelo gateway em vez de seguir com o TTL presumido na captura da URL.
    if (response.data.expires_in) {
      localStorage.setItem('expiresIn', String(response.data.expires_in));
    }

    console.log('token refreshed');

    return newAccessToken;
  } catch (error: any) {
    console.log(error?.message);

    throw error;
  }
};

export const shouldGenerateNewToken: () => boolean = () => {
  const tokenCreatedAt = Number(localStorage.getItem('tokenCreatedAt'));
  const tokenExpiresIn = Number(localStorage.getItem('expiresIn')) * 1000;

  const difference = Date.now() - tokenCreatedAt;

  const percentage = 0.25;

  return difference > percentage * tokenExpiresIn;
};

apiSensedia.interceptors.request.use(
  function (config) {
    if (
      !config.url?.includes('/access-token') &&
      (window.location.pathname.includes('/corretor') ||
        window.location.pathname.includes('/assessoria'))
    ) {
      config.headers!['Authorization'] = `Bearer ${getAccessToken()}`;
      config.headers!['user-data-info'] = userDataToken();
    }
    return config;
  },
  function (error) {
    console.error('Request Error:', error);

    return Promise.reject(error);
  }
);

apiSensedia.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.log('interceptou erro', error.config);
    const originalRequest = error.config;

    const responseData = error.response?.data;

    // A Sensedia devolve a falha de token como texto; o Apigee devolve
    // {"fault":{"detail":{"errorcode":"oauth.v2.InvalidAccessToken"}}}.
    const isTokenInvalid =
      error.response?.status === 401 &&
      ((typeof responseData === 'string' &&
        responseData.includes(
          'Access Token in the request, identified by HEADER access_token, is invalid'
        )) ||
        String(responseData?.fault?.detail?.errorcode ?? '').startsWith(
          'oauth.v2.'
        ));

    console.log('verificou se foi erro de token', error.response);

    if (isTokenInvalid && !originalRequest._retry) {
      originalRequest._retry = true;

      // O token do Apigee vive ~300s, então aqui ele costuma já estar vencido: renova
      // antes de repetir em vez de só esperar o cron do App.tsx renovar.
      try {
        await refreshToken();
      } catch {
        console.log('esperando...');
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      console.log('tentando novamente');
      // Retry com o token que já deve estar atualizado
      originalRequest.headers['Authorization'] = `Bearer ${getAccessToken()}`;
      return apiSensedia(originalRequest);
    }

    return Promise.reject(error);
  }
);
